const key = (v: string) => v.trim().toLowerCase();

/**
 * Next selection for a pie-chart filter click.
 *
 * Plain click: exclusive select, or clear when it is already the only value.
 * ⌘-click / long-press: toggle the value. With nothing selected the pie shows
 * everything, so the toggle removes the value ("all except this"). Selecting
 * every value collapses back to no filter.
 */
export function togglePieSelection(
    current: readonly string[],
    value: string,
    allValues: readonly string[],
    multiSelect: boolean
): string[] {
    const k = key(value);
    const has = current.some((v) => key(v) === k);
    if (!multiSelect) {
        return current.length === 1 && has ? [] : [value];
    }
    const base = current.length === 0 ? allValues : current;
    const next = base.some((v) => key(v) === k)
        ? base.filter((v) => key(v) !== k)
        : [...base, value];
    const allKeys = new Set(allValues.map(key));
    const coversAll = allKeys.size > 0 && [...allKeys].every((a) => next.some((v) => key(v) === a));
    return coversAll ? [] : next;
}

/**
 * Chip text for a selection. Shows "All except …" when the selection is the
 * known universe minus fewer values than are selected.
 */
export function formatSelection(
    selected: readonly string[],
    allValues: readonly string[] | undefined,
    format: (v: string) => string = (v) => v,
    separator = ", "
): string {
    const listed = selected.map(format).join(separator);
    if (!allValues || allValues.length === 0) return listed;
    const selectedKeys = new Set(selected.map(key));
    const allKeys = new Set(allValues.map(key));
    if (![...selectedKeys].every((k) => allKeys.has(k))) return listed;
    const missing = allValues.filter((v) => !selectedKeys.has(key(v)));
    if (missing.length === 0 || missing.length >= selected.length) return listed;
    return `All except ${missing.map(format).join(separator)}`;
}

export function splitSelection(value: string | undefined | null, separator = ","): string[] {
    return (value || "")
        .split(separator)
        .map((v) => v.trim())
        .filter(Boolean);
}
